import * as React from "react";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import Box from "@amzn/awsui-components-react/polaris/box";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import CopyToClipboard from "@amzn/awsui-components-react/polaris/copy-to-clipboard";
import { API } from "aws-amplify";
import PageShell from "../../../../global/components/PageShell";

export const RECIPE_VIEWER_PATH = "/cpcp-v2-recipe";

export function recipeViewerUrl(recipeKey: string): string {
  return `${RECIPE_VIEWER_PATH}?key=${encodeURIComponent(recipeKey)}`;
}

export function formatRecipeContent(content: string): string {
  try {
    return JSON.stringify(JSON.parse(content), null, 2);
  } catch {
    return content;
  }
}

interface APIError extends Error {
  response?: { status?: number; data?: { message?: string } };
}

export function recipeErrorMessage(e: unknown): string {
  const err = e as APIError | undefined;
  return err?.response?.data?.message || err?.message || "Failed to load recipe";
}

interface State {
  recipeKey: string;
  content: string | null;
  loading: boolean;
  error: string | null;
}

class RecipeViewer extends React.Component<any, State> {
  constructor(props: any) {
    super(props);
    const params = new URLSearchParams(props.location?.search || "");
    this.state = {
      recipeKey: params.get("key") || "",
      content: null,
      loading: true,
      error: null,
    };
  }

  componentDidMount() { this.loadRecipe(); }

  loadRecipe = async () => {
    const { recipeKey } = this.state;
    if (!recipeKey) {
      this.setState({ error: "No recipe key provided", loading: false });
      return;
    }
    try {
      const result = await API.get("CpcpV2API", `/cpcp/v2/recipes/${encodeURIComponent(recipeKey)}`, {});
      this.setState({ content: formatRecipeContent(result.content || ""), loading: false });
    } catch (e) {
      this.setState({ error: recipeErrorMessage(e), loading: false });
    }
  };

  renderBody() {
    const { recipeKey, content, loading, error } = this.state;
    if (loading) return <Spinner size="large" />;
    if (error) return <Alert type="error" header="Could not load recipe">{error}</Alert>;
    return (
      <Container
        header={
          <Header variant="h2"
            description="Read-only view. Edit recipes through the Recipe Builder."
            actions={
              <CopyToClipboard
                copyButtonText="Copy JSON"
                copySuccessText="Copied!"
                copyErrorText="Failed to copy"
                textToCopy={content || ""}
              />
            }>
            {recipeKey}
          </Header>
        }>
        <pre style={{
          backgroundColor: "#f5f5f5",
          border: "1px solid lightgray",
          borderRadius: "4px",
          padding: "10px",
          overflowX: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "normal",
        }}>
          {content}
        </pre>
      </Container>
    );
  }

  render() {
    return (
      <PageShell
        content={
          <Box padding={{ top: "l", bottom: "l" }}>
            {this.renderBody()}
          </Box>
        }
      />
    );
  }
}

export default RecipeViewer;
